import { __awaiter } from "tslib";
import { get } from 'svelte/store';
import { Container } from './container';
import { AppState } from '../etc/stores';
import { StatefulService } from './stateful-service';
import { Customer } from './customer';
import { ReferralProgram } from './referral-program';
import { CodeVerification } from './code-verification';
import { CustomerImport } from './customer-import';
import { AUTH_STORAGE_FLAGS, SessionManager } from './session-manager';
import Analytics from './analytics';
import { Api, extractMessageFromError } from './api';
import jwt_decode from 'jwt-decode';
import sessionKeys from '../etc/sessionKeys';
export class Auth extends StatefulService {
    constructor() {
        super(...arguments);
        this.api = this.ctx.get(Api).create(process.env.API_BASE_URL);
        this.customer = this.ctx.get(Customer);
        this.referralProgram = this.ctx.get(ReferralProgram);
        this.codeVerification = this.ctx.get(CodeVerification);
        this.customerImport = this.ctx.get(CustomerImport);
        this.session = this.ctx.get(SessionManager);
    }
    loadState() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            this.session.remove(sessionKeys.GUEST);
            this.updateWebsiteFlowGuest(true);
            const authInit = yield this.getAuthState();
            AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, state.auth), { token: authInit.token, account: authInit.account }) })));
            const auth = yield this.backend.authenticate(authInit);
            if (auth === null || auth === void 0 ? void 0 : auth.access_token) {
                try {
                    yield this.handleMountFlow({ auth, authInit });
                }
                catch (e) {
                    Analytics.logEvent('login-error', 'join-state', ['segmentio'], {
                        message: extractMessageFromError(e),
                        authData: {
                            hasAccessToken: !!(auth === null || auth === void 0 ? void 0 : auth.access_token),
                            providerUserId: ((_a = auth === null || auth === void 0 ? void 0 : auth.providerData) === null || _a === void 0 ? void 0 : _a.uid) || null
                        },
                        authStore: {
                            account: (authInit === null || authInit === void 0 ? void 0 : authInit.account) || null
                        },
                        hasSoftLoginToken: !!localStorage.getItem(sessionKeys.AUTH_SOFT_LOGIN)
                    });
                }
            }
            else {
                const join = ((_b = auth === null || auth === void 0 ? void 0 : auth.providerData) === null || _b === void 0 ? void 0 : _b.email) || ((_c = auth === null || auth === void 0 ? void 0 : auth.providerData) === null || _c === void 0 ? void 0 : _c.phone);
                this.updateWebsiteFlowGuest(!join);
                AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, (state.auth || {})), { providerData: auth === null || auth === void 0 ? void 0 : auth.providerData, connected: false }) })));
            }
        });
    }
    updateWebsiteFlowGuest(guest) {
        AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { authenticatedOnWebsite: !guest, flowGuestSignIn: guest }) })));
    }
    registerEventListeners() {
        this.registerWebsiteEvents();
    }
    signup(createCustomerProfile) {
        return __awaiter(this, void 0, void 0, function* () {
            let { category, prefixEvent } = Analytics.getConnectAccountData();
            const { website } = this.getAppState();
            const authFlow = website.authFlow;
            switch (authFlow) {
                case 'free-coins':
                    prefixEvent = authFlow;
                    category = 'free-coins-state';
                    break;
                case 'referred-redeem':
                    prefixEvent = authFlow;
                    category = 'referred-redeem';
                    break;
                case 'customer-import':
                    prefixEvent = authFlow;
                    category = 'customer-import-redeem';
                    break;
            }
            Analytics.logEvent(`${prefixEvent}-create-account`, category);
            const transactionToken = this.session.get(sessionKeys.TRANSACTION_TOKEN);
            const data = Object.assign(Object.assign({}, createCustomerProfile), (transactionToken ? { transactionToken } : {}));
            const response = yield this.postSignUpToken(data);
            yield this.authWithAccessToken(response.data.auth.access_token);
            this.session.remove(sessionKeys.TRANSACTION_TOKEN);
            Analytics.logEvent(`${prefixEvent}-create-account-success`, category);
            this.session.remove(sessionKeys.COLLECT_COINS);
            this.session.remove(sessionKeys.GUEST);
        });
    }
    signin(codeValidation, method) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!codeValidation)
                return;
            const { category, prefixEvent } = Analytics.getConnectAccountData('signin-state');
            const transactionToken = this.session.get(sessionKeys.TRANSACTION_TOKEN);
            const profileData = {
                firstName: method.name,
                lastName: method.lastname,
                email: method.value
            };
            Analytics.logEvent(`${prefixEvent}-validate`, category);
            yield this.codeValidation(Object.assign(Object.assign({}, profileData), { code: codeValidation, source: 'account-by-email', id: method.validationCode.id }));
            const response = yield this.postSignUpToken(Object.assign(Object.assign({}, profileData), { transactionToken: transactionToken }));
            Analytics.logEvent(`${prefixEvent}-validate-success`, category);
            yield this.authWithAccessToken(response.data.auth.access_token);
            this.session.remove(sessionKeys.TRANSACTION_TOKEN);
        });
    }
    authWithAccessToken(access_token, loadCustomerData = true) {
        return __awaiter(this, void 0, void 0, function* () {
            this.backend.api.setBearerToken(access_token);
            AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, state.auth), { connected: true, access_token }) })));
            try {
                if (loadCustomerData) {
                    yield this.referralProgram.loadState();
                    yield this.customer.loadState();
                }
            }
            catch (error) { }
        });
    }
    handleConnectAccount(override = {}) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const checkout = this.session.get(sessionKeys.CHECKOUT);
            if (checkout) {
                const { auth, customer } = this.getAppState();
                const postSignUpData = {
                    email: ((_a = auth.providerData) === null || _a === void 0 ? void 0 : _a.email) ||
                        customer.mail ||
                        checkout.contactEmail ||
                        override.email,
                    firstName: ((_b = auth.providerData) === null || _b === void 0 ? void 0 : _b.firstName) ||
                        customer.name ||
                        checkout.contactFirstName,
                    lastName: ((_c = auth.providerData) === null || _c === void 0 ? void 0 : _c.lastName) ||
                        customer.lastname ||
                        checkout.contactLastName,
                    transactionToken: checkout === null || checkout === void 0 ? void 0 : checkout.transactionToken
                };
                if (postSignUpData.firstName && postSignUpData.lastName) {
                    yield this.postSignUpToken(postSignUpData);
                }
            }
            return this.connectAccount(override);
        });
    }
    connectAccount(override = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const authInit = yield this.getAuthState();
            this.session.remove(sessionKeys.CHECKOUT);
            this.session.remove(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL);
            this.session.remove(sessionKeys.COLLECT_COINS);
            let auth = yield this.backend.authenticate(Object.assign(Object.assign({}, authInit), (override.login && override.loginType === 'phone'
                ? { phone: override.login }
                : {})));
            return this.handleMountFlow({
                auth,
                override,
                authInit,
                params: {
                    joining: true
                }
            });
        });
    }
    handleMountFlow({ auth, override = {}, authInit, params }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { website: { connectingAccount } } = get(AppState);
            const isSoftLogin = !authInit.token && auth.access_token;
            const next = isSoftLogin
                ? { next: 'wallet' }
                : yield this.getSignUpToken(authInit.token, authInit.account);
            const { flow, method, loginType, navigationOptions } = yield this.getFlowFromAuthState(next);
            let codeId = '';
            const authenticatedOnWebsite = yield this.isAuthenticatedOnWebsite();
            if (auth.access_token) {
                yield this.authWithAccessToken(auth.access_token, !!(params && params.joining));
            }
            AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, state.auth), { account: authInit.account, token: authInit.token }), website: Object.assign(Object.assign({}, state.website), { checkout: false, lightIcon: false, connectingAccount, requestEmailVerification: false, flow,
                    loginType, login: override.login, method,
                    codeId,
                    authenticatedOnWebsite }) })));
            return { route: flow, navigationOptions };
        });
    }
    finishAuthFlow(navigate) {
        return __awaiter(this, void 0, void 0, function* () {
            const { website: { authFlow }, customerImportCampaign } = this.getAppState();
            if (authFlow === 'customer-import') {
                const customerImportRedeemResponse = yield this.customerImport.customerImportRedeem();
                navigate('/customer-import-redeem-status', {
                    state: Object.assign(Object.assign({}, customerImportRedeemResponse), { coins: Number(customerImportCampaign.coinsReward).toFixed(0) })
                });
                return;
            }
            let category;
            if (authFlow === 'referred-redeem') {
                category = 'referredCoin';
            }
            if (authFlow === 'free-coins') {
                category = 'welcomeCoinAvailable';
            }
            if (authFlow === 'fast-redemption') {
                category = 'fastRedemption';
            }
            if (category) {
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { connectingAccount: true, popoverOpen: false, category }) })));
                return;
            }
            return navigate('/dashboard');
        });
    }
    getFlowFromAuthState(response) {
        return __awaiter(this, void 0, void 0, function* () {
            const { customerImportCampaign, website: { authFlow } } = get(AppState);
            const isCustomerImportFlow = authFlow === 'customer-import';
            switch (response.next) {
                /**
                 * User has a Twism account
                 */
                case 'wallet':
                    const customerImportRedeemResponse = yield this.customerImport.customerImportRedeem();
                    return Object.assign({ flow: isCustomerImportFlow && customerImportRedeemResponse
                            ? '/customer-import-redeem-status'
                            : '/dashboard', loginType: 'email', method: 'signin' }, (isCustomerImportFlow &&
                        customerImportRedeemResponse && {
                        navigationOptions: {
                            state: Object.assign(Object.assign({}, customerImportRedeemResponse), { coins: Number(customerImportCampaign.coinsReward).toFixed(0) })
                        }
                    }));
                case 'name-form':
                    /**
                     * Wix provide email or phone,
                     * and any belongs to a Twism account
                     */
                    return {
                        flow: '/connect-account',
                        loginType: 'email',
                        method: 'signup',
                        navigationOptions: {
                            state: {
                                userData: {
                                    email: response.data.email,
                                    name: response.data.firstName,
                                    lastname: response.data.lastName
                                }
                            }
                        }
                    };
                case 'code-validation-form':
                    return {
                        flow: '/code',
                        navigationOptions: {
                            state: {
                                method: {
                                    type: 'email',
                                    value: response.data.email,
                                    name: response.data.firstName,
                                    lastname: response.data.lastName,
                                    validationCode: response.data.validationCode
                                }
                            }
                        },
                        loginType: 'email',
                        method: 'signin'
                    };
                default:
                    return {
                        flow: '/connect-account',
                        loginType: 'email',
                        method: 'signup'
                    };
            }
        });
    }
    requestCodeCustomer(auth) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const authState = auth || this.getAppState().auth;
            if (((_a = authState.signInOptions) === null || _a === void 0 ? void 0 : _a.length) === 1) {
                const { customerId, type } = authState.signInOptions[0];
                const codeId = yield this.codeVerification.sendCodeToCustomer(customerId, type);
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { codeId }) })));
                return codeId;
            }
        });
    }
    requestCodeTarget(target, type) {
        return __awaiter(this, void 0, void 0, function* () {
            const codeId = yield this.codeVerification.sendCodeToTarget(target, type);
            AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { codeId }) })));
            return codeId;
        });
    }
    getSignUpToken(token, account) {
        return __awaiter(this, void 0, void 0, function* () {
            const { body } = yield this.api.get(`/widget-connect/${this.provider}/account/by-token?token=${token}&account=${account}`);
            return body;
        });
    }
    codeValidation(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const { body } = yield this.api.post(`/widget-connect/code-validation`, {
                body: data
            });
            return body;
        });
    }
    postSignUpToken(data) {
        return __awaiter(this, void 0, void 0, function* () {
            const { token, account } = yield this.getAuthState();
            const { customerImportCampaign } = get(AppState);
            const businessReferralLinkId = this.session.get(sessionKeys.BUSINESS_REFERRAL_LINK_ID);
            const urlParams = new URLSearchParams(location.search);
            const campaign = urlParams.get('twism-campaign');
            const customerAcquisition = this.session.get(AUTH_STORAGE_FLAGS.customerAcquisition);
            const customerImportId = (customerImportCampaign === null || customerImportCampaign === void 0 ? void 0 : customerImportCampaign.id) ||
                this.session.get(AUTH_STORAGE_FLAGS.customerImport);
            const { body } = yield this.api.post(`/widget-connect/${this.provider}/connect/by-token`, {
                body: Object.assign(Object.assign(Object.assign(Object.assign({}, data), { token,
                    account,
                    businessReferralLinkId }), (customerImportId && {
                    invitationCampaignId: customerImportId
                })), ((campaign === 'customer-acquisition' || customerAcquisition) && {
                    customerAcquisitionCampaign: true
                }))
            });
            urlParams.delete('twism-campaign');
            this.session.remove(AUTH_STORAGE_FLAGS.customerAcquisition);
            window.history.replaceState(null, null, window.location.pathname + urlParams.toString());
            if (businessReferralLinkId)
                this.session.remove(sessionKeys.BUSINESS_REFERRAL_LINK_ID);
            return body;
        });
    }
    isSoftLogin() {
        return __awaiter(this, void 0, void 0, function* () {
            const { auth, customer } = get(AppState);
            if (!auth.access_token)
                return false;
            const payload = jwt_decode(auth.access_token);
            return payload.extraInfo.isSoftUser && !customer.isMailVerified;
        });
    }
}
Container.add(Auth);
